import * as React from 'react';
import dayjs from 'dayjs';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    TextField,
} from '@mui/material';
import {TimePicker} from '@mui/x-date-pickers';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {deleteTaskSeriesQuery, updateTaskSeriesQuery} from '../../services/taskService';
import {SeriesScope} from '../../types/task';
import {queryKeys} from '../../data/queryKeys';
import {useRefreshBoardTasks} from '../../data/tasks';
import {apiErrorMessage} from '../../utilities/apiError';
import {useBoard} from '../../data/board';
import {LIMITS} from '../../contract';

// How many tasks each scope would touch, read from the board's already-loaded task
// list (null when that list isn't loaded, in which case no counts are shown).
function useSeriesCounts(task: any) {
    const queryClient = useQueryClient();
    const tasks = queryClient.getQueryData<any[]>(queryKeys.tasks(task.boardId));
    if (!tasks) return null;

    const series = tasks.filter((t) => t.seriesId === task.seriesId);
    return {
        all: series.length,
        following: task.date ? series.filter((t) => t.date && t.date >= task.date).length : 1,
    };
}


type DeleteProps = {
    task: any;
    onClose: () => void;
    onDeleteThisOnly: () => void;
};

export function DeleteSeriesDialog({task, onClose, onDeleteThisOnly}: DeleteProps) {
    const queryClient = useQueryClient();
    const refreshTasks = useRefreshBoardTasks(task.boardId);
    const counts = useSeriesCounts(task);
    const [scope, setScope] = React.useState<'this' | SeriesScope>('this');

    const {mutate: deleteSeries, isPending, error} = useMutation({
        mutationFn: (seriesScope: SeriesScope) => deleteTaskSeriesQuery(task.id, seriesScope),
        onSuccess: ({deletedTaskIds}) => {
            refreshTasks();
            onClose();
        },
    });

    function confirm() {
        if (scope === 'this') {
            onDeleteThisOnly();
            onClose();
            return;
        }
        deleteSeries(scope);
    }

    const countLabel = (n?: number) => (n === undefined ? '' : ` (${n})`);

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Delete recurring task</DialogTitle>

            <DialogContent>
                <DialogContentText sx={{mb: 1}}>
                    "{task.title || 'Untitled'}" is part of a recurring series. Which tasks should be deleted?
                </DialogContentText>

                <RadioGroup value={scope} onChange={(e) => setScope(e.target.value as 'this' | SeriesScope)}>
                    <FormControlLabel value="this" control={<Radio/>} label="This task only"/>
                    <FormControlLabel value="following" control={<Radio/>} label={`This and following tasks${countLabel(counts?.following)}`}/>
                    <FormControlLabel value="all" control={<Radio/>} label={`All tasks in the series${countLabel(counts?.all)}`}/>
                </RadioGroup>

                {error && <Alert severity="error" sx={{mt: 1}}>{apiErrorMessage(error)}</Alert>}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button color="error" variant="contained" disabled={isPending} onClick={confirm}>
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );
}

type DeleteWholeProps = {
    // Any task of the series.
    task: any;
    // What the series is called, as the board shows it.
    name: string;
    onClose: () => void;
};

// Deleting a recurring task entirely: every occurrence, wherever it is on the board. (The dialog above is for one task of a series,
// and asks how much of the series to delete.)
export function DeleteWholeSeriesDialog({task, name, onClose}: DeleteWholeProps) {
    const refreshTasks = useRefreshBoardTasks(task.boardId);
    const counts = useSeriesCounts(task);

    const {mutate: deleteSeries, isPending, error} = useMutation({
        mutationFn: () => deleteTaskSeriesQuery(task.id, 'all'),
        onSuccess: () => {
            refreshTasks();
            onClose();
        },
    });

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Delete "{name}"?</DialogTitle>

            <DialogContent>
                <DialogContentText>
                    {counts ? `This permanently deletes all ${counts.all} tasks in this recurring series` : 'This permanently deletes every task in this recurring series'},
                    {' '}in every section, including any already archived. It can't be undone.
                </DialogContentText>

                {error && <Alert severity="error" sx={{mt: 1}}>{apiErrorMessage(error)}</Alert>}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button color="error" variant="contained" disabled={isPending} onClick={() => deleteSeries()}>
                    Delete series
                </Button>
            </DialogActions>
        </Dialog>
    );
}

type EditProps = {
    task: any;
    onClose: () => void;
};

export function EditSeriesDialog({task, onClose}: EditProps) {
    const refreshTasks = useRefreshBoardTasks(task.boardId);
    const counts = useSeriesCounts(task);
    const {data: board} = useBoard(task.boardId, {fetch: false});
    const sections = (board?.sectionOrder ?? []).map((sectionId) => ({id: sectionId, title: board?.sectionsById?.[sectionId]?.title || 'Untitled section'}));

    const originalTime = React.useMemo(() => (task.date ? dayjs(task.date) : null), [task.date]);
    const [title, setTitle] = React.useState<string>(task.title ?? '');
    const [time, setTime] = React.useState(originalTime);
    const [description, setDescription] = React.useState<string>(task.description ?? '');
    const [sectionId, setSectionId] = React.useState<string>(task.sectionId ?? '');
    const [scope, setScope] = React.useState<SeriesScope>('following');

    const minutesOfDay = (d: dayjs.Dayjs) => d.hour() * 60 + d.minute();
    const timeIsValid = !time || time.isValid();
    const shiftMinutes = originalTime && time && time.isValid() ? minutesOfDay(time) - minutesOfDay(originalTime) : 0;
    const titleChanged = title.trim() !== (task.title ?? '');
    const descriptionChanged = description.trim() !== (task.description ?? '').trim();
    const sectionChanged = !!sectionId && sectionId !== task.sectionId;
    const canSave = timeIsValid && !!title.trim() && (titleChanged || descriptionChanged || sectionChanged || shiftMinutes !== 0);

    const {mutate: saveSeries, isPending, error} = useMutation({
        mutationFn: () => updateTaskSeriesQuery(task.id, scope, {
            title: titleChanged ? title.trim() : undefined,
            description: descriptionChanged ? description.trim() : undefined,
            sectionId: sectionChanged ? sectionId : undefined,
            shiftMinutes: shiftMinutes !== 0 ? shiftMinutes : undefined,
        }),
        onSuccess: () => {
            refreshTasks();
            onClose();
        },
    });

    const countLabel = (n?: number) => (n === undefined ? '' : ` (${n})`);

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Edit recurring series</DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    <TextField label="Task name" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth/>

                    <TextField
                        label="Description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        minRows={2}
                        maxRows={6}
                        fullWidth
                        slotProps={{htmlInput: {maxLength: LIMITS.description}}}
                        helperText={`${description.length} / ${LIMITS.description}. Where, who with, or what to bring. The spoken summaries use it.`}
                    />

                    {sections.length > 1 && (
                        <TextField select label="Section" value={sectionId} onChange={(e) => setSectionId(e.target.value)} fullWidth>
                            {sections.map((section) => (
                                <MenuItem key={section.id} value={section.id}>{section.title}</MenuItem>
                            ))}
                        </TextField>
                    )}

                    {originalTime && (
                        <TimePicker label="Time" value={time} onChange={(value) => setTime(value)}/>
                    )}

                    <RadioGroup value={scope} onChange={(e) => setScope(e.target.value as SeriesScope)}>
                        <FormControlLabel value="following" control={<Radio/>} label={`This and following tasks${countLabel(counts?.following)}`}/>
                        <FormControlLabel value="all" control={<Radio/>} label={`All tasks in the series${countLabel(counts?.all)}`}/>
                    </RadioGroup>

                    <DialogContentText variant="caption">
                        Each task keeps its own day. To move a single task to another day, change it on its own.
                    </DialogContentText>

                    {error && <Alert severity="error">{apiErrorMessage(error)}</Alert>}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={!canSave || isPending} onClick={() => saveSeries()}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
